import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { handleStartProcessCustomEvent } from 'src/components/EntityPresentation/utils/handleStartProcessCustomEvent';
import { FormioForm } from 'src/components/FormioForm/FormioForm';
import { SpinnerFullWidth } from 'src/components/Spinner/SpinnerFullWidth';
import { Router } from 'src/core/router/Router';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { CombinedLoadingStatus } from 'src/utils/mobx/CombinedLoadingStatus';

export type EntityProfileEditProps = {
    store: BasicStore<any>;
    entityListUrl: string;
};
export const EntityProfileEdit = observer(
    ({ store, entityListUrl }: EntityProfileEditProps) => {
        const { params, url } = useRouteMatch<{ entityId: string }>();
        const { entityId } = params;

        const entityUrl = `${entityListUrl}/${entityId}`;

        useEffect(() => {
            if (!entityId) {
                return;
            }
            store.loadItem(entityId);
            store.loadForm(store.itemEditFormKey);
            store.updateItemLoader.reset();

            return () => {
                store.formLoader.reset();
            };
        }, [entityId]);

        const loadingStatus = useMemo(() => {
            return new CombinedLoadingStatus([
                store.formLoader,
                store.itemLoader,
            ]);
        }, []);

        if (loadingStatus.isLoading) {
            return <SpinnerFullWidth />;
        }

        return (
            <FormioForm
                key={`${entityId}-edit`}
                form={store.currentItemForm as any}
                submission={{ data: store.currentItem } as any}
                onCustomEvent={(event) => {
                    if (event.type === 'startProcess') {
                        handleStartProcessCustomEvent(event, url);
                    } else if (event.type === 'cancel') {
                        Router.navigate(entityUrl);
                    }

                    return Promise.resolve();
                }}
                onSubmit={async (submission) => {
                    const newItem = await store.update(
                        entityId,
                        submission.data,
                    );

                    if (newItem) {
                        await store.loadList();
                        Router.navigate(`${entityListUrl}/me`);
                    }
                }}
            />
        );
    },
);
